import {
  ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  type WaterUseBenchmarkCatalogSnapshot,
} from '../../types/economicContribution';

export const WATER_USE_BENCHMARK_CATALOG_VERSION = 'water-use-benchmark-catalog-2026-09-02-v8' as const;
export const ASHRAE_NOMINAL_COOLING_TOWER_TON_BTU_PER_HOUR = 15_000 as const;

/**
 * Versioned public-source inputs for early-stage water-use screening.
 * Values here are benchmark coefficients, never observations of the subject
 * site. Changing a coefficient or source requires a catalog-version change.
 */
export const WATER_USE_BENCHMARK_CATALOG = {
  schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  catalogVersion: WATER_USE_BENCHMARK_CATALOG_VERSION,
  createdAt: '2026-09-02',
  sources: [
    {
      id: 'lbnl-us-data-center-energy-use-2024-water',
      title: '2024 United States Data Center Energy Usage Report',
      publisher: 'Lawrence Berkeley National Laboratory',
      publisherClass: 'federal-laboratory',
      url: 'https://eta-publications.lbl.gov/sites/default/files/2024-12/lbnl-2024-united-states-data-center-energy-usage-report_1.pdf',
      publicationDate: '2024-12',
      accessedAt: '2026-08-28',
      excerpt: 'The average WUE, which stays just over 0.36 L/kWh through 2023.',
      locator: 'Report p. 48, Figure 4.7 discussion; methods and scenario bounds on pp. 38-44',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'numeric-basis',
      notes: 'National modeled site-WUE series. The benchmark uses the reported through-2023 average only; the 0.45-0.48 L/kWh future projection is not blended into a subject-site range.',
    },
    {
      id: 'doe-femp-data-center-wue-definition',
      title: 'Cooling Water Efficiency Opportunities for Federal Data Centers',
      publisher: 'U.S. Department of Energy, Federal Energy Management Program',
      publisherClass: 'federal-agency',
      url: 'https://www.energy.gov/cmei/femp/cooling-water-efficiency-opportunities-federal-data-centers',
      accessedAt: '2026-08-28',
      excerpt: 'WUE = Annual Site Water Usage (liters) / IT Equipment Annual Energy Use (kWh).',
      locator: 'Water Usage Effectiveness section',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'independent-cross-check',
      notes: 'Supplies the canonical WUE numerator and IT-energy denominator; it does not supply the numeric benchmark band.',
    },
    {
      id: 'lbnl-site-climate-cooling-model-2024',
      title: '2024 United States Data Center Energy Usage Report',
      publisher: 'Lawrence Berkeley National Laboratory',
      publisherClass: 'federal-laboratory',
      url: 'https://eta-publications.lbl.gov/sites/default/files/2024-12/lbnl-2024-united-states-data-center-energy-usage-report.pdf',
      publicationDate: '2024-12',
      accessedAt: '2026-09-01',
      excerpt: 'Climate data from 965 weather stations were collected.',
      locator: 'Report pp. 40-43, cooling systems, space types, and location-specific TMY simulation method',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'independent-cross-check',
      notes: 'Supports treating climate, data-center type, and cooling-system type as explicit model context. It does not provide a transferable monthly climate multiplier.',
    },
    {
      id: 'doe-femp-climate-economizer-controls',
      title: 'Cooling Water Efficiency Opportunities for Federal Data Centers',
      publisher: 'U.S. Department of Energy, Federal Energy Management Program',
      publisherClass: 'federal-agency',
      url: 'https://www.energy.gov/cmei/femp/cooling-water-efficiency-opportunities-federal-data-centers',
      publicationDate: '2019-01-09',
      accessedAt: '2026-09-01',
      excerpt: 'Savings will depend on several variables, including climate, data center temperature and humidity set points, and the number of hours.',
      locator: 'Use of Air-Side Economizing Strategies and Use of Water-Side Economizing Strategies',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'independent-cross-check',
      notes: 'Supports requiring set points, controls, and operating hours before converting local ambient conditions into a numeric economizer or hybrid wet-mode adjustment.',
    },
    {
      id: 'ashrae-ai-dry-cooling-integration',
      title: 'AI Data Center Energy Performance Framework: Integrated Design Principles',
      publisher: 'ASHRAE',
      publisherClass: 'standards-body',
      url: 'https://www.ashrae.org/technical-resources/ai-data-center-framework/integrated-design-principles',
      accessedAt: '2026-09-01',
      excerpt: 'The system supports facility water inlet temperatures up to 45°C/113°F.',
      locator: 'Integration with Dry Coolers and System Safeguards',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'independent-cross-check',
      notes: 'Supports a high-temperature liquid-cooling reference case and conditional adiabatic assist. It is not a universal dry-bulb cutoff or a project equipment curve.',
    },
    {
      id: 'ashrae-liquid-cooling-classes-2023',
      title: 'ASHRAE Handbook: Data Centers and Telecommunication Facilities',
      publisher: 'ASHRAE',
      publisherClass: 'standards-body',
      url: 'https://handbook.ashrae.org/Handbooks/A23/SI/A23_Ch20/a23_ch20_si.aspx',
      publicationDate: '2023',
      accessedAt: '2026-09-01',
      excerpt: 'uses a simple numerical value in the class name to represent the maximum facility supply liquid temperature',
      locator: 'Chapter 20, Environmental Guidelines for Liquid-Cooled Equipment, paragraphs preceding Table 2',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'numeric-basis',
      notes: 'A class limit is an IT-equipment operating envelope, not a project set point or proof of dry-cooler capacity.',
    },
    {
      id: 'lbnl-warm-water-dry-cooler-approach',
      title: 'Hot for Warm Water Cooling',
      publisher: 'Lawrence Berkeley National Laboratory',
      publisherClass: 'federal-laboratory',
      url: 'https://datacenters.lbl.gov/sites/default/files/Hot%20for%20Warm%20Water%20Cooling.pdf',
      publicationDate: '2011',
      accessedAt: '2026-09-01',
      excerpt: 'Table 2 lists 5.5°C for a dry-fin cooler, 2.77°C for a CDU, and 1.67°C for a plate-and-frame exchanger.',
      locator: 'Printed p. 4, Section 4 and Table 2; qualification on printed p. 6',
      excerptKind: 'table-transcription',
      evidenceRole: 'numeric-basis',
      notes: 'The reference dry-cooler construct includes one CDU; the upper screening path also allows one additional plate-and-frame exchanger. These are published reference assumptions, not OEM guarantees.',
    },
    {
      id: 'ashrae-dry-cooler-economic-approach-2024',
      title: 'ASHRAE Handbook: Cooling Towers',
      publisher: 'ASHRAE',
      publisherClass: 'standards-body',
      url: 'https://handbook.ashrae.org/Handbooks/S24/SI/S24_Ch40/S24_Ch40_si.aspx',
      publicationDate: '2024',
      accessedAt: '2026-09-01',
      excerpt: 'They can economically cool water to within approximately 11 K of the ambient dry-bulb temperature.',
      locator: 'Chapter 40, opening discussion',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'numeric-basis',
      notes: 'The 11 K value is an economic reference for reasonable-size equipment, not a required minimum approach or vendor guarantee.',
    },
    {
      id: 'ashrae-adiabatic-effectiveness-range',
      title: 'ASHRAE Handbook: Evaporative Air-Cooling Equipment',
      publisher: 'ASHRAE',
      publisherClass: 'standards-body',
      url: 'https://handbook.ashrae.org/Handbooks/S20/IP/s20_ch41/s20_ch41_ip.aspx',
      publicationDate: '2020',
      accessedAt: '2026-09-01',
      excerpt: 'Saturation effectiveness varies from 70% to over 95%, depending on media depth and air velocity.',
      locator: 'Chapter 41, direct saturation efficiency discussion',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'numeric-basis',
      notes: 'The model uses a narrower 75%-90% sensitivity inside this published range; actual performance must come from the selected manufacturer.',
    },
    {
      id: 'lbnl-dry-cooling-water-sensitivity-2024',
      title: '2024 United States Data Center Energy Usage Report',
      publisher: 'Lawrence Berkeley National Laboratory',
      publisherClass: 'federal-laboratory',
      url: 'https://eta-publications.lbl.gov/sites/default/files/2024-12/lbnl-2024-united-states-data-center-energy-usage-report_1.pdf',
      publicationDate: '2024-12',
      accessedAt: '2026-08-28',
      excerpt: 'While air-cooled chillers use no water, they use more energy.',
      locator: 'Report p. 46, PUE/WUE results discussion following Figure 4.4',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'numeric-basis',
      notes: 'Supports zero direct evaporative cooling-process water for waterless air-cooled rejection, not zero total site water or zero source water.',
    },
    {
      id: 'doe-femp-hybrid-data-center-cooling',
      title: 'Cooling Water Efficiency Opportunities for Federal Data Centers',
      publisher: 'U.S. Department of Energy, Federal Energy Management Program',
      publisherClass: 'federal-agency',
      url: 'https://www.energy.gov/cmei/femp/cooling-water-efficiency-opportunities-federal-data-centers',
      publicationDate: '2019-01-09',
      accessedAt: '2026-09-01',
      excerpt: 'The system is also able to operate in series and use the evaporative cooling process in conjunction with the air-cooled heat rejection system.',
      locator: 'Technology Options, Direct Liquid Cooling Systems, hybrid thermosyphon discussion',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'independent-cross-check',
      notes: 'Supports presenting hybrid wet and dry operating modes separately; it does not supply a transferable wet-mode fraction or subject-site water factor.',
    },
    {
      id: 'aws-sustainability-report-2024-water',
      title: '2024 Amazon Sustainability Report, AWS Summary',
      publisher: 'Amazon',
      publisherClass: 'operator-primary',
      url: 'https://sustainability.aboutamazon.com/2024-amazon-sustainability-report-aws-summary.pdf',
      publicationDate: '2025',
      accessedAt: '2026-09-02',
      excerpt: '2024 AWS global PUE: 1.15; global data-center WUE: 0.15 L/kWh.',
      locator: 'AWS metric summary p. 3; Carbon and Energy p. 4; Water, AWS Data Centers p. 7',
      excerptKind: 'table-transcription',
      evidenceRole: 'numeric-basis',
      notes: 'Global operator-fleet observation; not a prediction or actual value for the subject site.',
    },
    {
      id: 'microsoft-datacenter-efficiency-fy2025',
      title: 'Datacenter sustainability and efficiency',
      publisher: 'Microsoft',
      publisherClass: 'operator-primary',
      url: 'https://datacenters.microsoft.com/sustainability/efficiency/',
      publicationDate: '2025',
      accessedAt: '2026-08-28',
      excerpt: 'FY2025 Americas PUE: 1.16; WUE: 0.34 L/kWh.',
      locator: 'Datacenter efficiency table and methodology footnote',
      excerptKind: 'table-transcription',
      evidenceRole: 'independent-cross-check',
      notes: 'Americas operator-fleet observation for owned and controlled sites operating for at least 12 months.',
    },
    {
      id: 'meta-environmental-data-index-2025-water',
      title: 'Meta 2025 Environmental Data Index',
      publisher: 'Meta',
      publisherClass: 'operator-primary',
      url: 'https://sustainability.atmeta.com/wp-content/uploads/2025/10/Meta_2025-Environmental-Data-Index.pdf',
      publicationDate: '2025',
      accessedAt: '2026-08-28',
      excerpt: '2024 annual WUE: 0.19 L/kWh; WUE uses water withdrawal divided by IT electricity load.',
      locator: 'Report pp. 9 and 17',
      excerptKind: 'table-transcription',
      evidenceRole: 'independent-cross-check',
      notes: 'Operator-fleet observation with an explicit withdrawal numerator; it is not a subject-site actual.',
    },
    {
      id: 'epa-watersense-cooling-tower-2017',
      title: 'WaterSense at Work: Mechanical Systems',
      publisher: 'U.S. Environmental Protection Agency',
      publisherClass: 'federal-agency',
      url: 'https://www.epa.gov/sites/default/files/2017-12/documents/ws-commercialbuildings-waterscore-mechanical-systems-guide.pdf',
      publicationDate: '2017-11',
      accessedAt: '2026-08-28',
      excerpt: 'Cooling Tower Water Use (Make-Up) = Evaporation + Blowdown.',
      locator: 'PDF pp. 4-6, Equation 2 and Figure 2',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'numeric-basis',
      notes: 'Figure 2 gives 1.8 gal/ton-hour evaporation and 2.25-2.70 gal/ton-hour makeup at 5-3 cycles of concentration.',
    },
    {
      id: 'ashrae-nominal-cooling-tower-ton-2024',
      title: 'ASHRAE Handbook: Cooling Towers',
      publisher: 'ASHRAE',
      publisherClass: 'standards-body',
      url: 'https://handbook.ashrae.org/Handbooks/S24/IP/s24_ch40/s24_ch40_ip.aspx',
      publicationDate: '2024',
      accessedAt: '2026-09-02',
      excerpt: 'At these conditions, the cooling tower rejects 15,000 Btu/h per nominal cooling tower ton.',
      locator: 'Chapter 40, Section 2, Design Conditions, nominal cooling-tower capacity discussion',
      excerptKind: 'verbatim-quote',
      evidenceRole: 'numeric-basis',
      notes: 'Defines the heat-rejection denominator paired with the EPA gallons-per-ton-hour coefficient. It is distinct from the 12,000 Btu/h evaporator ton and is a screening convention, not an equipment rating for this site.',
    },
  ],
  defaults: {
    pue: {
      low: 1.15,
      high: 1.30,
      source: 'screening-assumption',
      sourceLabel: 'Published modern-fleet values plus a conservative early-stage upper sensitivity',
      sourceRefIds: [
        'aws-sustainability-report-2024-water',
        'microsoft-datacenter-efficiency-fy2025',
        'lbnl-us-data-center-energy-use-2024-water',
      ],
    },
    utilization: {
      low: 0.5,
      high: 1,
      source: 'screening-assumption',
      sourceLabel: '50%-100% annual-load sensitivity; not a claim about expected utilization',
      sourceRefIds: ['lbnl-us-data-center-energy-use-2024-water'],
    },
  },
  wueBenchmarks: [
    {
      id: 'modern-operator-fleet-wue-v1',
      label: 'Total site water · published modern operator-fleet benchmark',
      scenario: 'modern-operator-fleet',
      lowLitersPerKwhIt: 0.15,
      highLitersPerKwhIt: 0.34,
      flowBasis: 'unspecified',
      sourceRefIds: [
        'aws-sustainability-report-2024-water',
        'meta-environmental-data-index-2025-water',
        'microsoft-datacenter-efficiency-fy2025',
        'doe-femp-data-center-wue-definition',
      ],
      limitations: [
        'The WUE numerator is total annual onsite water use, not cooling-tower water alone; exact operator reporting boundaries can differ.',
        'The endpoints are observations from different operator fleets, climates, cooling designs, reporting boundaries, and years.',
        'The sources do not all use an identical withdrawal-versus-consumption numerator, so the flow basis remains unspecified.',
      ],
    },
    {
      id: 'lbnl-national-current-average-wue-v2',
      label: 'Total site water · LBNL 2023 national average benchmark',
      scenario: 'national-current-average',
      lowLitersPerKwhIt: 0.36,
      highLitersPerKwhIt: 0.36,
      flowBasis: 'consumption',
      sourceRefIds: [
        'lbnl-us-data-center-energy-use-2024-water',
        'doe-femp-data-center-wue-definition',
      ],
      limitations: [
        'The site-WUE numerator represents total onsite water use and must not be added to a cooling-tower row.',
        'This is a national modeled average, not a site-specific cooling-design calculation or a range of observed facilities.',
        'The post-2023 0.45-0.48 L/kWh projection is deliberately excluded; it is a future national scenario, not a current subject-site upper bound.',
        'Actual performance can fall outside the band because climate, heat rejection, controls, and utilization differ.',
      ],
    },
  ],
  wetTower: {
    id: 'epa-wet-cooling-tower-water-v2',
    nominalCoolingTowerTonBtuPerHour:
      ASHRAE_NOMINAL_COOLING_TOWER_TON_BTU_PER_HOUR,
    evaporationGallonsPerTonHour: 1.8,
    makeupGallonsPerTonHour: { low: 2.25, high: 2.70 },
    defaultCyclesOfConcentration: { low: 3, high: 5 },
    sourceRefIds: [
      'epa-watersense-cooling-tower-2017',
      'ashrae-nominal-cooling-tower-ton-2024',
      'doe-femp-hybrid-data-center-cooling',
    ],
    limitations: [
      'The sensitivity assumes essentially all facility electrical input becomes heat rejected through the modeled cooling tower.',
      'EPA gallons-per-ton-hour coefficients are converted from total heat rejection using the ASHRAE 15,000 Btu/h nominal cooling-tower ton, not the 12,000 Btu/h evaporator ton.',
      'Drift, leaks, water chemistry, economizer hours, redundancy, weather, and part-load controls can change makeup demand.',
    ],
  },
  dryCoolingScreening: {
    waterClasses: [
      { id: 'W32', maxFacilitySupplyC: 32 },
      { id: 'W40', maxFacilitySupplyC: 40 },
      { id: 'W45', maxFacilitySupplyC: 45 },
    ],
    dryCoolerApproachC: { low: 5.5, high: 11 },
    cduApproachC: 2.77,
    additionalPlateHeatExchangerApproachC: { low: 0, high: 1.67 },
    adiabaticSaturationEffectiveness: { low: 0.75, high: 0.9 },
    sourceRefIds: [
      'ashrae-liquid-cooling-classes-2023',
      'lbnl-warm-water-dry-cooler-approach',
      'ashrae-dry-cooler-economic-approach-2024',
      'ashrae-adiabatic-effectiveness-range',
      'ashrae-ai-dry-cooling-integration',
    ],
    limitations: [
      'This compares local screening temperatures (an SED peak dry-bulb proxy and a screening wet bulb, not ASHRAE design percentiles) with typical published ranges; it is not an OEM capacity selection, redundancy check, or thermal guarantee.',
      'W32-W45 are warm-water liquid-cooling references. Air-cooled rooms and residual air heat require a separate architecture-specific check.',
      'Peak dry bulb and the screening wet bulb may not be coincident in the source record, so the adiabatic result is a sensitivity rather than an hourly design point.',
      'The reference path includes one 2.77°C CDU allowance and a 0-1.67°C additional plate-and-frame exchanger allowance; the actual topology and equipment approaches must be verified.',
      'Glycol, altitude, fouling, recirculation, fan-power, and N+1 margins remain unmodeled.',
      'With hourly TMY weather, the screen estimates annual adiabatic water using disclosed generic airflow, effectiveness, activation, and cycles-of-concentration assumptions. An OEM or design quantity still requires project equipment curves, airflow, controls, setpoints, redundancy, and water-quality inputs.',
    ],
  },
} as const satisfies WaterUseBenchmarkCatalogSnapshot;

/** Mechanical integrity checks for the runtime benchmark artifact. */
export function validateWaterUseBenchmarkCatalog(
  catalog: WaterUseBenchmarkCatalogSnapshot,
): string[] {
  const errors: string[] = [];
  const finite = (value: number) => Number.isFinite(value);
  if (catalog.schemaVersion !== ECONOMIC_CONTRIBUTION_SCHEMA_VERSION) {
    errors.push(`Unsupported water benchmark schema version: ${catalog.schemaVersion}.`);
  }
  if (!catalog.catalogVersion.trim()) errors.push('Water benchmark catalog version is required.');
  if (!catalog.createdAt.trim()) errors.push('Water benchmark catalog creation date is required.');
  const sourceIds = new Set<string>();
  for (const source of catalog.sources) {
    if (sourceIds.has(source.id)) errors.push(`Duplicate source id: ${source.id}.`);
    sourceIds.add(source.id);
    if (!source.url.startsWith('https://')) errors.push(`${source.id}: source URL must use HTTPS.`);
    if (!source.excerpt.trim()) errors.push(`${source.id}: source excerpt is required.`);
    if (!source.locator.trim()) errors.push(`${source.id}: source locator is required.`);
  }

  const validateRefs = (owner: string, refs: readonly string[]) => {
    if (refs.length === 0) errors.push(`${owner}: at least one source reference is required.`);
    refs.forEach((id) => {
      if (!sourceIds.has(id)) errors.push(`${owner}: missing source ${id}.`);
    });
  };

  for (const benchmark of catalog.wueBenchmarks) {
    if (!finite(benchmark.lowLitersPerKwhIt)
      || !finite(benchmark.highLitersPerKwhIt)
      || !(benchmark.lowLitersPerKwhIt > 0)
      || !(benchmark.highLitersPerKwhIt >= benchmark.lowLitersPerKwhIt)) {
      errors.push(`${benchmark.id}: WUE range must be finite, positive, and ordered.`);
    }
    validateRefs(benchmark.id, benchmark.sourceRefIds);
    if (benchmark.scenario === 'modern-operator-fleet') {
      const independentPublishers = new Set(
        benchmark.sourceRefIds
          .map((id) => catalog.sources.find((source) => source.id === id)?.publisher)
          .filter((publisher): publisher is string => publisher != null),
      );
      if (independentPublishers.size < 2) {
        errors.push(`${benchmark.id}: the headline operator band requires two independent publishers.`);
      }
    }
  }

  const { wetTower, dryCoolingScreening, defaults } = catalog;
  if (wetTower.nominalCoolingTowerTonBtuPerHour
    !== ASHRAE_NOMINAL_COOLING_TOWER_TON_BTU_PER_HOUR) {
    errors.push(
      `${wetTower.id}: nominal cooling-tower-ton heat-rejection basis must be ${String(ASHRAE_NOMINAL_COOLING_TOWER_TON_BTU_PER_HOUR)} Btu/h.`,
    );
  }
  if (!finite(wetTower.evaporationGallonsPerTonHour)
    || !finite(wetTower.makeupGallonsPerTonHour.low)
    || !finite(wetTower.makeupGallonsPerTonHour.high)
    || !(wetTower.evaporationGallonsPerTonHour > 0)
    || !(wetTower.makeupGallonsPerTonHour.low >= wetTower.evaporationGallonsPerTonHour)
    || !(wetTower.makeupGallonsPerTonHour.high >= wetTower.makeupGallonsPerTonHour.low)) {
    errors.push(`${wetTower.id}: cooling-tower coefficients are invalid.`);
  }
  if (!finite(wetTower.defaultCyclesOfConcentration.low)
    || !finite(wetTower.defaultCyclesOfConcentration.high)
    || !(wetTower.defaultCyclesOfConcentration.low > 1)
    || !(wetTower.defaultCyclesOfConcentration.high >= wetTower.defaultCyclesOfConcentration.low)) {
    errors.push(`${wetTower.id}: cycles of concentration must be greater than one and ordered.`);
  }
  validateRefs(wetTower.id, wetTower.sourceRefIds);
  if (dryCoolingScreening.waterClasses.length === 0) {
    errors.push('Dry-cooling screening requires at least one facility-water class.');
  }
  const dryClassIds = new Set<string>();
  for (const waterClass of dryCoolingScreening.waterClasses) {
    if (dryClassIds.has(waterClass.id)) {
      errors.push(`Duplicate dry-cooling water class: ${waterClass.id}.`);
    }
    dryClassIds.add(waterClass.id);
    if (!finite(waterClass.maxFacilitySupplyC)) {
      errors.push(`${waterClass.id}: maximum facility supply temperature must be finite.`);
    }
  }
  if (!finite(dryCoolingScreening.dryCoolerApproachC.low)
    || !finite(dryCoolingScreening.dryCoolerApproachC.high)
    || !(dryCoolingScreening.dryCoolerApproachC.low > 0)
    || !(dryCoolingScreening.dryCoolerApproachC.high >= dryCoolingScreening.dryCoolerApproachC.low)) {
    errors.push('Dry-cooler approach range must be finite, positive, and ordered.');
  }
  if (!finite(dryCoolingScreening.cduApproachC)
    || !(dryCoolingScreening.cduApproachC > 0)) {
    errors.push('CDU approach must be finite and positive.');
  }
  if (!finite(dryCoolingScreening.additionalPlateHeatExchangerApproachC.low)
    || !finite(dryCoolingScreening.additionalPlateHeatExchangerApproachC.high)
    || !(dryCoolingScreening.additionalPlateHeatExchangerApproachC.low >= 0)
    || !(dryCoolingScreening.additionalPlateHeatExchangerApproachC.high
      >= dryCoolingScreening.additionalPlateHeatExchangerApproachC.low)) {
    errors.push('Additional plate heat-exchanger approach range must be finite, nonnegative, and ordered.');
  }
  if (!finite(dryCoolingScreening.adiabaticSaturationEffectiveness.low)
    || !finite(dryCoolingScreening.adiabaticSaturationEffectiveness.high)
    || !(dryCoolingScreening.adiabaticSaturationEffectiveness.low > 0)
    || !(dryCoolingScreening.adiabaticSaturationEffectiveness.high
      >= dryCoolingScreening.adiabaticSaturationEffectiveness.low)
    || !(dryCoolingScreening.adiabaticSaturationEffectiveness.high < 1)) {
    errors.push('Adiabatic saturation-effectiveness range must be within (0, 1) and ordered.');
  }
  validateRefs('dry-cooling-screening', dryCoolingScreening.sourceRefIds);
  validateRefs('default-pue', defaults.pue.sourceRefIds);
  validateRefs('default-utilization', defaults.utilization.sourceRefIds);
  if (!finite(defaults.pue.low)
    || !finite(defaults.pue.high)
    || !(defaults.pue.low >= 1)
    || !(defaults.pue.high >= defaults.pue.low)) {
    errors.push('Default PUE range must be at least one and ordered.');
  }
  if (!finite(defaults.utilization.low)
    || !finite(defaults.utilization.high)
    || !(defaults.utilization.low > 0)
    || !(defaults.utilization.high >= defaults.utilization.low)
    || !(defaults.utilization.high <= 1)) {
    errors.push('Default utilization range must be within (0, 1] and ordered.');
  }
  return errors;
}
